import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  Type,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EaDisplay } from '@app/components/pr-display';
import {
  PrTable,
  PrTableColumn,
} from '@app/components/pr-table/pr-table.component';
import { SimulationDao } from '@app/dao/simulation/simulation.dao';
import { SimulationType } from '@app/dao/simulation/simulation.interface';
import { UserDao } from '@app/dao/user/user.dao';
import {
  SimulationInformations,
  SimulationParameters,
} from '@app/dashboard/shared/ea-simulation-parameters/ea-simulation-parameters.component';
import { EaSimulationPacksComponent } from '@app/dashboard/sheets/shared/ea-simulation-packs/ea-simulation-packs.component';
import {
  SimulationPack,
  SimulationPackService,
} from '@app/dashboard/sheets/shared/ea-simulation-packs/simulation-pack.service';
import { EaSimulationAvFormComponent } from '@app/forms/simulation/ea-simulation-av-form/ea-simulation-av-form.component';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { leavePackStepWithoutPack } from '@app/forms/subscription/ea-subscription-forms/pledge-pack-choice';
import { ProductsService, ProductsType } from '@app/providers/products.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { throwError, catchError, finalize, map, shareReplay, take, takeWhile } from 'rxjs';

export interface PropaDataAv {
  investAmount: number;
  currentAge: number;
  retireAge: number;
  risk: string;
}
export interface AvPayload {
  id?: string;
  allocationModelId?: any;
  productId?: any;
  dependentChildCount?: number;
  durationInYears?: number;
  expenseList?: any;
  incomeList?: any;
  initialDeposit?: number;
  mainResidence?: any;
  maritalStatus?: string;
  monthlyDeposit?: number;
  personalUseList?: any;
  phoneNumber?: any;
  rentalAssetList?: any;
  riskProfile?: any;
  riskProfileScore?: number;
  supportList?: any;
  taxAmount?: number;
  taxDeductionReductionList?: any;
  taxShareCount?: number;
  tmi?: number;
  totalActualCosts?: number;
  totalIncome?: number;
}

const DEFAULT_PARAMETERS: AvPayload = {};
@UntilDestroy()
@Component({
    selector: 'simulation-packs-av',
    templateUrl: './simulation-packs.component.html',
    styleUrls: ['./simulation-packs.component.scss'],
    standalone: false
})
export class SimulationPacksComponent implements AfterViewInit, OnDestroy {
  packService: string = 'life_insurance';
  productsType = ProductsType;
  form: Type<any> = EaSimulationAvFormComponent;
  formOptions;

  defaultParameters: Array<SimulationParameters | SimulationInformations> = [
    {
      label: "Paramètres de l'investissement",
      parameters: [
        {
          icon: 'sp-simu-cash',
          label: 'Versement initial',
          tooltip: 'Versement initial',
          value: simulation => simulation.parameters.initialDeposit,
          suffix: '€',
        },
        {
          icon: 'sp-simu-cash',
          label: 'Versements réguliers',
          tooltip: 'Versements réguliers',
          value: simulation => simulation.parameters.monthlyDeposit,
          suffix: '€',
        },
        {
          icon: 'sp-simu-gavel',
          label: 'Frais d’entrée',
          tooltip: 'Frais d’entrée',
          value: simulation =>
            simulation.parameters.entryFee
              ? Math.round(simulation.parameters.entryFee * 100)
              : 0,
          suffix: '%',
        },
        {
          icon: 'sp-simu-gavel',
          label: 'Niveau de risque',
          tooltip: 'Niveau de risque',
          value: simulation => simulation.parameters.riskProfileScore,
          suffix: '/ 7',
        },
      ],
    },
    {
      label:
        'On peut investir en assurance vie soit avec un versement ponctuel, un versement régulier ou les deux',
      content:
        "La stratégie d'investissement peut-etre différenciée entre un versement ponctuel ou régulier. Votre conseiller peut vous orienter dans vos choix",
      display: EaDisplay.success,
    },
  ];
  parameters = this.defaultParameters;
  loading: boolean = true;
  simulation: any;
  @ViewChild(EaSimulationPacksComponent, { read: SimulationPackService })
  simulationPackService: SimulationPackService;
  packsTableColumnsDefinition: PrTableColumn[];
  lineOptions;
  _ = _;
  score = undefined;

  private alive = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductsService,
    private userDao: UserDao,

    private simulationDao: SimulationDao,
    public cd: ChangeDetectorRef,
    private subscriptionFacade: SubscriptionFacadeService,
  ) {}

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    this.packService = pledge.productType === 'PER' ? 'per' : 'life_insurance';
    const owners = pledge.owners;
    this.userDao
      .getAuthentified()
      .pipe(take(1))
      .subscribe(user => {
        const { score } =
          owners?.find(owner => owner.id === user.id) || owners?.[0] || {};

        this.score = score;
      });
  }
  ngAfterViewInit() {
    this.cd.detectChanges();

    this.simulation = { parameters: DEFAULT_PARAMETERS };

    this.simulate(DEFAULT_PARAMETERS)
      .pipe(
        untilDestroyed(this),
        takeWhile(() => this.alive),
        shareReplay(1),
      )
      .subscribe();
    this.simulate({
      supportList: this.turnPackAllocationToDTO(),
      allocationModelId: undefined,
      productId: undefined,
      id: null,
    });
    this.simulationPackService.allocationFormSubject
      .pipe(
        untilDestroyed(this),
        takeWhile(() => this.alive),
        shareReplay(1),
      )
      .subscribe(() => {
        this.simulate({
          supportList: this.turnPackAllocationToDTO(),
          allocationModelId: undefined,
          productId: undefined,
          id: null,
        })
          .pipe(untilDestroyed(this))
          .subscribe(() => {
            this.cd.detectChanges();
          });
      });
  }

  simulate(payload?: AvPayload, id?: any) {
    this.loading = true;
    let productChanged =
      payload &&
      payload.productId &&
      this.simulation.parameters.productId != payload.productId;

    this.simulation.parameters = {
      ...this.simulation.parameters,
      ...payload,
      productId: this.subscriptionFacade.getPledge().products[0].productId,
    };
    if (productChanged) {
      this.simulation.parameters.allocationModelId = null;
      this.simulation.parameters.supportList = null;
    }
    if (
      !this.simulation.parameters.supportList &&
      this.simulationPackService.selectedPack
    ) {
      if (this.simulationPackService.selectedPack.id) {
        this.simulation.parameters.supportList =
          this.simulationPackService.selectedPack.allocation;
      } else {
        this.simulation.parameters.allocationModelId =
          this.simulationPackService.selectedPack.allocation;
      }
    }

    return this.simulationDao
      .simulate(
        // id ? id : this.simulation.id,
        payload && payload.hasOwnProperty('id')
          ? payload.id
          : id
            ? id
            : this.simulation.id,

        this.subscriptionFacade.getPledge().productType === 'PER'
          ? SimulationType.PER
          : SimulationType.ASSURANCE_VIE,
        this.simulation.parameters,
      )
      .pipe(
        untilDestroyed(this),

        takeWhile(() => this.alive),
        catchError(() => {
          if (id) {
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { id: null },
              queryParamsHandling: 'merge',
            });

            return throwError(
              'Error when loading simulation, reloading page without id param',
            );
          } else {
            this.setSimulation(this.simulation);
            this.onPackSelected(this.simulationPackService.packs[0]);
            return throwError('No pack for this product');
          }
        }),
        map(simulation => {
          this.setSimulation(simulation);
        }),
        // shareReplay(1),
        finalize(() => {
          this.loading = false;
        }),
      );
  }

  setSimulation(simulation) {
    this.simulation = simulation;

    /* table setup */
    this.simulationPackService.tableDefinitionColumn = [
      new PrTableColumn('NOM DES SUPPORTS', 'name', '', {
        value: val => val.support.label,
        span: 2,
        tooltip: true,
      }),
      new PrTableColumn('CODE ISIN', 'isinCode', {
        value: val => val.support.isinCode,
        tooltip: true,
      }),
      new PrTableColumn('PERF. SUR 3 ANS', 'perfP3Y', 'percent', {
        value: val =>
          this.displayRateAsPercentage(val.support.performances.perfP3Y),
        hasTotal: true,
        customTotalValue: `${this.displayRateAsPercentage(
          this.simulation.result?.portfolio?.performances?.perfP3Y,
        )}`,
      }),
      new PrTableColumn('VOL. SUR 3 ANS', 'volat3Y', 'percent', {
        value: val =>
          this.displayRateAsPercentage(val.support.performances.volat3Y),
      }),
      new PrTableColumn('RISQUE', 'risk', '', {
        suffix: '/ 7',
        value: val =>
          val.support.profilRisk.value ? val.support.profilRisk.value : '-',
      }),
      new PrTableColumn('%', 'ponderation', '', {
        hasTotal: true,
        suffix: '%',
        value: val => Math.round(val.ponderation * 100),
      }),
    ];

    if (this.simulation.parameters.productId)
      this.simulationPackService.productId =
        this.simulation.parameters.productId;

    this.simulationPackService
      .getPacks()
      .pipe(
        untilDestroyed(this),
        takeWhile(() => this.alive),
      )
      .subscribe(packs => {
        leavePackStepWithoutPack(packs, this.subscriptionFacade);
        this.setPack(packs);
        if (this.simulation.parameters.allocationModelId) {
          this.setPack(packs);
        }
        this.simulationPackService.setTableData(
          this.simulation?.result?.portfolio?.composition || [],
        );

        this.loading = false;
        this.cd.detectChanges();
      });
  }
  setPack(packs: any) {
    const packId =
      this.subscriptionFacade.getPledge()?.products[0]?.parameters
        ?.productPackId;

    const pack = packs?.find(pack => pack.id === packId);

    !this.simulationPackService.selectedPack &&
      this.simulationPackService.selectPack(pack);
  }

  private displayRateAsPercentage(rate: number): string {
    if (rate === null || rate === undefined) {
      return '-';
    }

    return Math.round(rate * 10000) / 100 + '%';
  }

  onPackSelected(pack: SimulationPack) {
    this.simulationPackService.selectPack(pack);
  }

  turnPackAllocationToDTO() {
    return (this.simulation.parameters.supportList = _.map(
      this.simulationPackService?.selectedPack?.allocation,
      (allocation: any) => {
        return {
          isinCode: allocation.supportIsin || allocation.isin,
          allocationPercentage: allocation.allocationPercentage,
        };
      },
    ));
  }

  onParametersEdit(event) {
    if (event.productId != this.simulation.parameters.productId)
      this.simulation.parameters.allocationModelId = undefined;
    this.simulate(event)
      .pipe(takeWhile(() => this.alive))
      .subscribe();
  }

  linePatrimony;

  initLineOptions() {
    let currentYear = new Date().getFullYear();
    let xCategories = _.range(currentYear, currentYear + 16);

    let series = [];

    series.push({
      name: 'Fourchette haute',
      color: '#33A02C',
      data: this.result.projection.minAmountsByProbability['0.1'],
    });

    series.push({
      name: 'Médiane',
      color: '#FDBF6F',
      data: this.result.projection.minAmountsByProbability['0.5'],
    });
    series.push({
      name: 'Fourchette basse',
      color: '#A6CEE3',
      data: this.result.projection.minAmountsByProbability['0.9'],
    });

    if (this.simulation.parameters.initialDeposit) {
      series.push({
        name: 'Somme investie',
        color: '#1F78B4',
        data: _.map(
          _.range(16),
          i => this.result.projection.spentAmountByYears[i],
        ),
      });
    }
    this.lineOptions = this.getLineOptions(series, xCategories);
  }

  getLineOptions(series: any[], xCategories: any[]): any {
    let options = {
      chart: {
        type: 'line',
        spacingLeft: 0,
        spacingRight: 0,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: xCategories,
      },
      yAxis: {
        title: {
          text: '',
        },
        allowDecimals: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        valueSuffix: '€',
        valueDecimals: 0,
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: false,
          },
          marker: {
            symbol: 'circle',
          },
        },
      },
      series: series,
    };

    return options;
  }

  projectionTable: PrTable;
  projectionTableDefinitionColumn = [
    new PrTableColumn('FOURCHETTE', 'name', ''),
    new PrTableColumn('5 ANS', '5Y', 'currency'),
    new PrTableColumn('8 ANS', '8Y', 'currency'),
    new PrTableColumn('10 ANS', '10Y', 'currency'),
    new PrTableColumn('12 ANS', '12Y', 'currency'),
    new PrTableColumn('15 ANS', '15Y', 'currency'),
  ];

  initProjectionTable() {
    let table = [
      {
        name: 'Fourchette haute',
        ...this.setProjectionTableProperties('0.1'),
      },
      {
        name: 'Médiane',
        ...this.setProjectionTableProperties('0.5'),
      },
      {
        name: 'Fourchette basse',
        ...this.setProjectionTableProperties('0.9'),
      },
      {
        name: 'Somme investie',
        '5Y': this.result.projection.spentAmountByYears[5],
        '8Y': this.result.projection.spentAmountByYears[8],
        '10Y': this.result.projection.spentAmountByYears[10],
        '12Y': this.result.projection.spentAmountByYears[12],
        '15Y': this.result.projection.spentAmountByYears[15],
      },
    ];

    this.projectionTable = new PrTable(
      table,
      this.projectionTableDefinitionColumn,
    );
  }

  //Shorthand table attribution
  setProjectionTableProperties(property) {
    return {
      '5Y': this.result.projection.minAmountsByProbability[property][5],
      '8Y': this.result.projection.minAmountsByProbability[property][8],
      '10Y': this.result.projection.minAmountsByProbability[property][10],
      '12Y': this.result.projection.minAmountsByProbability[property][12],
      '15Y': this.result.projection.minAmountsByProbability[property][15],
    };
  }

  ngOnDestroy() {
    this.alive = false;
  }

  get productType() {
    return this.productService
      .getProducts()
      .find(p => p.title == ProductsType.ASSURANCE_VIE);
  }

  get result() {
    return this.simulation && this.simulation.result;
  }
  extract() {
    const productId = this.subscriptionFacade.getPledge().products[0].productId;
    const pack = this.simulationPackService.selectedPack as any;
    const allocations = _.map(pack.allocation, allocation => {
      return {
        name: allocation.name,
        initalRepartition: allocation.allocationPercentage,
        // Math.floor(
        //   allocation.allocationPercentage * 100
        // )
        monthlyRepartition: allocation.allocationPercentage,
        // Math.floor(
        //   allocation.allocationPercentage * 100
        // )
        isin: allocation.supportIsin,
      };
    });
    return [
      {
        ownerId: null,
        productId: productId,
        parameters: {
          supportRepartition: allocations,
          productPackId: pack.id,
        },
      },
    ];
  }
  onSubmit() {
    if (this.simulationPackService.selectedPack) {
      // PRE-4896 : préremplit les 4 champs "*Autocomplete" de la slide "Taux par support" pour les
      // supports du pack sélectionné — sans quoi ils restent null pour ce chemin de sélection,
      // contrairement à la sélection libre (cf. support-form.component.ts).
      this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
      this.subscriptionFacade.submitPledgeWithoutForm(this);
    }
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
}
